/**
 * Первый вход: не график с десятками кнопок, а тишина и выбор.
 *
 * Четыре шага. Приветствие — тёмное пространство, где вдалеке идут линии
 * графиков. Выбор начала — не класс: откроется всё, мы лишь покажем
 * первым то, что интереснее. Две двери — виртуальный счёт сейчас, биржа
 * по API позже. И первая миссия, после которой человек сразу делает
 * дело, а не читает.
 *
 * Приветствие показывается один раз на профиль; «Пропустить» — на каждом
 * шаге: опытному человеку обучение не нужно.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react';
import type { JourneyPath } from '@specter/types';
import { useTerminal } from '../state/store.js';
import { NeonIcon } from '../ui/NeonIcon.js';
import { missionById, PATHS } from './missions.js';

type Step = 'hello' | 'path' | 'door' | 'mission';

export function Welcome(): JSX.Element | null {
  const loaded = useTerminal((s) => s.syncState !== 'loading');
  const welcomed = useTerminal((s) => s.journey.welcomed);
  const [step, setStep] = useState<Step>('hello');
  const [path, setPath] = useState<JourneyPath | null>(null);

  if (!loaded || welcomed) return null;

  const finish = (patch: { path?: JourneyPath | null; active?: string | null } = {}): void => {
    useTerminal.getState().patchJourney({ welcomed: true, path: patch.path ?? path, ...(patch.active !== undefined ? { active: patch.active } : {}) });
  };
  const first = missionById('move')!;

  return createPortal(
    <div className="hello" role="dialog" aria-modal="true" aria-label="Добро пожаловать в ANZONIX">
      <Lines />
      <button type="button" className="hello-skip" onClick={() => finish()}>
        Пропустить
      </button>

      {step === 'hello' && (
        <section className="hello-step hello-hello" key="hello">
          <p className="hello-brand">ANZONIX</p>
          <h1>Добро пожаловать в ANZONIX</h1>
          <p className="hello-lead">Построй свой мир трейдинга.</p>
          <p className="hello-sub">Сначала научись строить. Потом проверяй. Потом решай, запускать ли это в реальный рынок.</p>
          <button type="button" className="hello-go" onClick={() => setStep('path')}>
            Начать
          </button>
        </section>
      )}

      {step === 'path' && (
        <section className="hello-step" key="path">
          <p className="hello-kicker">Шаг 1 из 3</p>
          <h2>С чего начнём?</h2>
          <p className="hello-sub">Это не класс: в ANZONIX открыто всё. Мы просто покажем первым то, что вам интереснее.</p>
          <div className="hello-paths">
            {PATHS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={path === entry.id ? 'hello-path hello-path--on' : 'hello-path'}
                style={{ ['--tone' as string]: entry.tone }}
                onClick={() => setPath(entry.id)}
                aria-pressed={path === entry.id}
              >
                <span className="hello-path-icon" aria-hidden="true">
                  <NeonIcon name={entry.icon} size={30} />
                </span>
                <b>{entry.title}</b>
                <em>{entry.text}</em>
              </button>
            ))}
          </div>
          <button type="button" className="hello-go" disabled={path === null} onClick={() => setStep('door')}>
            Дальше
          </button>
        </section>
      )}

      {step === 'door' && (
        <section className="hello-step" key="door">
          <p className="hello-kicker">Шаг 2 из 3</p>
          <h2>Где будем торговать?</h2>
          <div className="hello-doors">
            <button type="button" className="hello-door hello-door--on" onClick={() => setStep('mission')}>
              <span className="hello-door-icon" aria-hidden="true">
                <NeonIcon name="flask" size={34} />
              </span>
              <b>Виртуальный счёт</b>
              <em>10 000 USDT ненастоящих денег на настоящих ценах. Пробуйте всё без риска: торговлю руками, ботов, стратегии.</em>
              <span className="hello-door-go">Войти →</span>
            </button>
            <div className="hello-door hello-door--soon" aria-disabled="true">
              <span className="hello-door-icon" aria-hidden="true">
                <NeonIcon name="lock" size={34} />
              </span>
              <b>Биржа по API</b>
              <em>Binance, Bybit, OKX — сделки из ANZONIX будут уходить на ваш счёт биржи. Готовим — сначала надёжность и безопасность ключей.</em>
              <span className="hello-door-soon">Скоро</span>
            </div>
          </div>
        </section>
      )}

      {step === 'mission' && (
        <section className="hello-step" key="mission">
          <p className="hello-kicker">Шаг 3 из 3 · первая миссия</p>
          <h2>{first.title}</h2>
          <div className="hello-mission">
            <span className="hello-mission-icon" aria-hidden="true">
              <NeonIcon name={first.icon} size={34} />
            </span>
            <p>
              Найдите момент, когда рынок начинает двигаться сильнее обычного, — и нажмите на эту свечу.
              <br />
              <em>Без лекций про свечные модели: смотрите на график и выбирайте. Платформа скажет, угадали ли вы.</em>
            </p>
            <span className="hello-xp">+{first.xp} опыта</span>
          </div>
          <button
            type="button"
            className="hello-go"
            onClick={() => {
              finish({ active: 'move' });
              window.location.hash = '#/chart';
            }}
          >
            К графику
          </button>
        </section>
      )}
    </div>,
    document.body,
  );
}

/**
 * Линии графиков вдалеке: три ломаные медленно плывут слева направо.
 * Не данные — настроение; при «меньше движения» стоят на месте.
 */
function Lines(): JSX.Element {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const ratio = window.devicePixelRatio || 1;
    let width = 0;
    let height = 0;
    const resize = (): void => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    // Случайное блуждание, заданное заранее: линии не дёргаются от кадра к кадру.
    const walks = [0.42, 0.58, 0.7].map((base, index) => {
      let value = 0;
      const points: number[] = [];
      let seed = 17 + index * 31;
      for (let i = 0; i < 240; i += 1) {
        seed = (seed * 16807) % 2147483647;
        value += ((seed / 2147483647) - 0.49) * 0.035;
        value *= 0.985;
        points.push(value);
      }
      return { base, points, tone: ['46, 230, 176', '167, 139, 250', '103, 232, 249'][index]!, speed: 0.012 + index * 0.006 };
    });
    let frame = 0;
    const draw = (time: number): void => {
      ctx.clearRect(0, 0, width, height);
      for (const walk of walks) {
        const shift = reduced ? 0 : (time * walk.speed) / 1000;
        const step = width / 120;
        ctx.beginPath();
        for (let i = 0; i <= 120; i += 1) {
          const index = Math.floor(i + shift * 20) % walk.points.length;
          const y = height * walk.base - walk.points[index]! * height * 1.4;
          if (i === 0) ctx.moveTo(0, y);
          else ctx.lineTo(i * step, y);
        }
        ctx.strokeStyle = `rgba(${walk.tone}, 0.22)`;
        ctx.lineWidth = 1.6;
        ctx.shadowColor = `rgba(${walk.tone}, 0.6)`;
        ctx.shadowBlur = 12;
        ctx.stroke();
      }
      if (!reduced) frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
    };
  }, []);
  return <canvas ref={ref} className="hello-lines" aria-hidden="true" />;
}
